import React from 'react';
import {
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardDescription,
  NativeCardContent,
  NativeButton
} from '../components/NativeComponents';
import { 
  FileText, 
  Download, 
  Book, 
  Server, 
  Shield, 
  Zap,
  Users,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DocumentationPage = () => {
  const { user, getEffectiveRole, isSuperAdmin, isAdmin } = useAuth();
  
  // Efektif rol kontrolü - simülasyon desteği
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  const isEffectiveAdmin = ['super_admin', 'admin'].includes(effectiveRole);
  
  // Tüm dökümanlar
  const allDocuments = [
    {
      id: 'sistem',
      title: 'Tam Sistem Dokümantasyonu',
      description: 'Tüm sistem bilgilerini içeren kapsamlı dokümantasyon',
      icon: Book,
      file: 'SISTEM_DOKUMANTASYONU.pdf',
      color: '#2563eb',
      featured: true,
      adminOnly: true // Sadece admin görebilir
    },
    {
      id: 'kullanici',
      title: 'Kullanıcı Kılavuzu',
      description: 'Sistem kullanımı için detaylı rehber',
      icon: Users,
      file: 'KULLANICI_KILAVUZU.pdf',
      color: '#059669',
      adminOnly: false // Herkes görebilir
    },
    {
      id: 'api',
      title: 'API Dokümantasyonu',
      description: 'Tüm API endpoint\'leri ve kullanım örnekleri',
      icon: Server,
      file: 'API_DOKUMANTASYONU.pdf',
      color: '#7c3aed',
      adminOnly: true
    },
    {
      id: 'teknik',
      title: 'Teknik Mimari',
      description: 'Sistem mimarisi ve veritabanı şeması',
      icon: FileText,
      file: 'TEKNIK_MIMARI.pdf',
      color: '#dc2626',
      adminOnly: true
    },
    {
      id: 'ocpp',
      title: 'OCPP Entegrasyon Rehberi',
      description: 'OCPP 1.6 protokolü ve cihaz bağlantısı',
      icon: Zap,
      file: 'OCPP_ENTEGRASYON_REHBERI.pdf',
      color: '#f59e0b',
      adminOnly: true
    },
    {
      id: 'deployment',
      title: 'Deployment Rehberi',
      description: 'Docker ile production kurulumu',
      icon: Server,
      file: 'DEPLOYMENT_GUIDE.pdf',
      color: '#0891b2',
      adminOnly: true
    },
    {
      id: 'roller',
      title: 'Rol ve Yetki Matrisi',
      description: 'Kullanıcı rolleri ve yetkilendirme detayları',
      icon: Shield,
      file: 'ROL_YETKI_MATRISI.pdf',
      color: '#be185d',
      adminOnly: false // Herkes görebilir
    }
  ];
  
  // Hesap Yöneticisi için filtreleme
  const documents = isEffectiveHesapYoneticisi 
    ? allDocuments.filter(doc => !doc.adminOnly)
    : allDocuments;

  const handleDownload = (file) => {
    window.open(`${API_URL}/docs/${file}`, '_blank');
  };

  return (
    <div data-testid="documentation-page" style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ 
          fontSize: '28px', 
          fontWeight: '700', 
          color: 'var(--text-primary)',
          marginBottom: '8px'
        }}>
          Sistem Dokümantasyonu
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
          Tüm teknik ve kullanıcı dokümantasyonlarına buradan erişebilirsiniz
        </p>
      </div>

      {/* Featured Document */}
      {documents.filter(d => d.featured).map(doc => (
        <NativeCard 
          key={doc.id}
          style={{ 
            marginBottom: '24px',
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            border: 'none'
          }}
        >
          <NativeCardContent style={{ padding: '32px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '16px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <doc.icon style={{ width: '32px', height: '32px', color: '#fff' }} />
                </div>
                <div>
                  <h2 style={{ 
                    fontSize: '22px', 
                    fontWeight: '700', 
                    color: '#fff',
                    marginBottom: '4px'
                  }}>
                    {doc.title}
                  </h2>
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                    {doc.description}
                  </p>
                </div>
              </div>
              <NativeButton 
                data-testid={`download-${doc.id}`}
                onClick={() => handleDownload(doc.file)}
                style={{
                  background: '#fff',
                  color: '#1e40af',
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: '600'
                }}
              >
                <Download style={{ width: '18px', height: '18px', marginRight: '8px' }} />
                PDF İndir
              </NativeButton>
            </div>
          </NativeCardContent>
        </NativeCard>
      ))}

      {/* Document Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '20px'
      }}>
        {documents.filter(d => !d.featured).map(doc => (
          <NativeCard 
            key={doc.id}
            data-testid={`doc-card-${doc.id}`}
            style={{ 
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            className="hover:shadow-lg"
          >
            <NativeCardContent style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: `${doc.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <doc.icon style={{ width: '24px', height: '24px', color: doc.color }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: 'var(--text-primary)',
                    marginBottom: '4px'
                  }}>
                    {doc.title}
                  </h3>
                  <p style={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: '13px',
                    marginBottom: '16px',
                    lineHeight: '1.5'
                  }}>
                    {doc.description}
                  </p>
                  <NativeButton 
                    data-testid={`download-${doc.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(doc.file)}
                    style={{ width: '100%' }}
                  >
                    <Download style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                    PDF İndir
                  </NativeButton>
                </div>
              </div>
            </NativeCardContent>
          </NativeCard>
        ))}
      </div>

      {/* Quick Access Info - Sadece Admin için */}
      {isEffectiveAdmin && (
      <NativeCard style={{ marginTop: '32px' }}>
        <NativeCardHeader>
          <NativeCardTitle>Hızlı Erişim Linkleri</NativeCardTitle>
          <NativeCardDescription>
            Sistem URL'leri ve bağlantı bilgileri
          </NativeCardDescription>
        </NativeCardHeader>
        <NativeCardContent>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            <div style={{
              padding: '16px',
              background: 'var(--card-bg)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ 
                fontSize: '13px', 
                fontWeight: '500', 
                color: 'var(--text-secondary)',
                marginBottom: '8px'
              }}>
                Web Arayüzü
              </div>
              <code style={{
                display: 'block',
                padding: '8px 12px',
                background: '#1e293b',
                color: '#94a3b8',
                borderRadius: '6px',
                fontSize: '13px',
                wordBreak: 'break-all'
              }}>
                {API_URL}
              </code>
            </div>
            
            <div style={{
              padding: '16px',
              background: 'var(--card-bg)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ 
                fontSize: '13px', 
                fontWeight: '500', 
                color: 'var(--text-secondary)',
                marginBottom: '8px'
              }}>
                API Base URL
              </div>
              <code style={{
                display: 'block',
                padding: '8px 12px',
                background: '#1e293b',
                color: '#94a3b8',
                borderRadius: '6px',
                fontSize: '13px',
                wordBreak: 'break-all'
              }}>
                {API_URL}/api
              </code>
            </div>
            
            <div style={{
              padding: '16px',
              background: 'var(--card-bg)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ 
                fontSize: '13px', 
                fontWeight: '500', 
                color: 'var(--text-secondary)',
                marginBottom: '8px'
              }}>
                OCPP WebSocket URL
              </div>
              <code style={{
                display: 'block',
                padding: '8px 12px',
                background: '#1e293b',
                color: '#22c55e',
                borderRadius: '6px',
                fontSize: '13px',
                wordBreak: 'break-all'
              }}>
                wss://{(API_URL || '').replace('https://', '').replace('http://', '')}/api/ws/ocpp/&#123;CIHAZ_ID&#125;
              </code>
            </div>
          </div>
        </NativeCardContent>
      </NativeCard>
      )}
    </div>
  );
};

export default DocumentationPage;
